import { type Address } from './address'
import { type SignatureRequest } from './signature-request'
import { StringBoolean } from 'lib/types/common'

export const PROBATE_SIGNATURE_REQUEST_DOCUMENTS = {
  TERMS_AND_CONDITIONS: 'terms_and_conditions',
  DEED_OF_RENUNCIATION: 'deed_of_renunciation',
}

export type ProbateSignatureRequestDocument =
  (typeof PROBATE_SIGNATURE_REQUEST_DOCUMENTS)[keyof typeof PROBATE_SIGNATURE_REQUEST_DOCUMENTS]

export const PROBATE_ESTATE_ITEM_TYPES = {
  BANK_ACCOUNT: 'bank_account',
  BUSINESS_OR_AGRICULTURAL_ASSET: 'business_or_agricultural_asset',
  CASH: 'cash',
  CREDIT_CARD: 'credit_card',
  INVESTMENT: 'investment',
  GIFT_GIVEN: 'gift_given',
  LIFE_INSURANCE: 'life_insurance',
  LOAN: 'loan',
  MONEY_OWED: 'money_owed',
  MORTGAGE: 'mortgage',
  NATIONAL_SAVINGS_AND_INVESTMENTS: 'national_savings_and_investments',
  OVERSEAS_ASSET: 'overseas_asset',
  PREMIUM_BOND: 'premium_bond',
  PRIVATE_PENSION: 'private_pension',
  PUBLIC_SHAREHOLDING: 'public_shareholding',
  UTILITY_OR_OTHER_DEBT: 'utility_or_other_debt',
  VEHICLE_OR_POSSESSION: 'vehicle_or_possession',
  GIFT_BENEFITTED: 'gift_benefitted',
  STATE_BENEFIT: 'state_benefit',
  TRUST_BENEFITTED: 'trust_benefitted',
} as const

export type ProbateEstateItemType =
  (typeof PROBATE_ESTATE_ITEM_TYPES)[keyof typeof PROBATE_ESTATE_ITEM_TYPES]

export type ProbateEstateAttributes = {
  completedSections: string[]
  createdAt: Date
  deathYear: number
  deathMonth: number
  deathDay: number
  nationalInsuranceNumber: string | null
  defaultNumberOfGrantsRequested: boolean
  externalExizentLastSentAt: Date | null
  hasCodicils: 'yes' | 'no'
  hasForeignWills: 'yes' | 'no'
  hasWill: boolean
  numberOfGrantsRequested: number
  numberOfOverseasGrantsRequested: number
  overseasGrantsRequested: boolean
  probateCaseId: number
  updatedAt: Date
  wasAdopted: boolean
  willFeelsUnsigned: boolean
  willHasCodicilReplacingExecutors: boolean
  willHasHandwrittenAmendments: boolean
  willHasInContemplationOfMarriageClause: boolean
  willHasIncorrectStatementsAboutWitnesses: boolean
  willHasIssuesWithCondition: boolean
  willHasShakySignature: boolean
  willHasTearsOrMarks: boolean
  willHasTrusts: boolean
  willHasUninitialledAmendments: boolean
  willIsDiy: boolean
  willIsUnbound: boolean
  willIsUndatedOrUnsigned: boolean
  title?: string | null
  placeOfDeath?: string | null
  occupation?: string | null
  deceasedNotes?: string | null
  willLocation?: string | null
  willOrIntestacyNotes?: string | null
  funeralBillPaid?: boolean | null
  funeralBillAmount?: string | null
  funeralAmountNotes?: string | null
  funeralSectionActive?: boolean
  utilityProvidersSectionActive?: boolean
  otherBillsSectionActive?: boolean
  dateOfBirth?: string | null
  dateOfDeath?: string | null
  lastPermanentAddress?: Address | null
  lastPermanentAddressId?: number | null
  householdPossessionsValue?: number | null
  cashFoundAtHome?: number | null
  householdItemsInsured?: string | null
  hasSafeDepositBox?: string | null
  personalPossessionsNotes?: string | null
  safeDepositBoxNotes?: string | null
  propertySectionActive?: boolean
  vehiclesAndPossessionsSectionActive?: boolean
  bankAccountsSectionActive?: boolean
  premiumBondsSectionActive?: boolean
  nationalSavingsAndInvestmentsSectionActive?: boolean
  publicShareholdingsSectionActive?: boolean
  advisorsSectionActive?: boolean
  lifeInsuranceSectionActive?: boolean
  privatePensionsSectionActive?: boolean
  stateBenefitsSectionActive?: boolean
  giftsSectionActive?: boolean
  trustsSectionActive?: boolean
  dependentsSectionActive?: boolean
  powerOfAttorneySectionActive?: boolean
  hadRightToBenefitFromTrustAssets?: 'yes' | 'no' | 'unsure' | null
  wasTrustee?: boolean | null
  trustAssetsNotes?: string | null
  hadPowerOfAttorneyOrDeputyship?: boolean | null
  powerOfAttorneyNotes?: string | null
  notesNotCoveredElsewhere?: string | null
}

export type ProbateEstate = {
  id: number
  attributes: ProbateEstateAttributes
}

type ProbateEstateFinancialsCategory = {
  items: { id: number; type: string; value: string }[]
  value: number
}

type ProbateEstateFinancials = {
  soleAssets: ProbateEstateFinancialsCategory
  soleDebts: ProbateEstateFinancialsCategory
  shareOfJointAssets: ProbateEstateFinancialsCategory
  debtsPayableOutOfJointAssets: ProbateEstateFinancialsCategory
  grossEstateNotRequiringGrant: ProbateEstateFinancialsCategory
  netEstateNotRequiringGrant: ProbateEstateFinancialsCategory
  grossEstateForGrant: ProbateEstateFinancialsCategory
  netEstateForGrant: ProbateEstateFinancialsCategory
  grossEstateForInheritanceTax: ProbateEstateFinancialsCategory
  netEstateForInheritanceTax: ProbateEstateFinancialsCategory
  unspecified: ProbateEstateFinancialsCategory
}

export type ProbateEstateSummary = {
  id: number
  type: 'probate_estate_summaries'
  attributes: {
    feeQuoted: number
    financials: ProbateEstateFinancials
    hasWill: boolean
    name: string
    taxForm: string | null
  }
}

export type ProbateEstateItemsAttributes = {
  itemType: ProbateEstateItemType
  probateEstateId: number
  probateEstatePropertyId: number
  description: string
  value: '' | number
  isEstimatedValue: boolean
  ownership: string
  jointOwnerName: string
  deceasedShareOfJointOwnership: number
  funeralHadPrePaidPlan: 'yes' | 'no' | 'unsure' | undefined
  funeralBillAmount: string
  funeralWakeAmount: string
  funeralFlowersAmount: string
  funeralOtherAmount: string
  funeralAmountCoveredByPlan: string
  funeralAmountNotes: string
  notes: string
  isDeleted: boolean
  providerName: string
  referenceNumber: string
  accountNumber: string
  sortCode: string
  stockbrokerName: string
  vehicleType: string | null
  vehicleCondition: string | null
  utilityType: string | null
  isInsurancePaidOut: boolean
  isLumpSumPensionPayout: boolean
  lumpSumPayableTo: string
  giftRecipient: string
  giftYear: number
  giftMonth: number
  giftDay: number
  probateEstate: ProbateEstate
}

export type ProbateEstateItem = {
  id: number
  type: 'probate_estate_items'
  attributes: Partial<ProbateEstateItemsAttributes>
}

export type ProbateEstatePeopleAttributes = {
  probateEstateId: number
  isCustomer: boolean
  isPointOfContact: boolean
  isPersonalRepresentative: boolean
  isIncludedInComms: boolean
  title: string
  firstNames: string
  lastName: string
  preferredName: string
  nameMatchesWill: boolean
  nameMismatchNotes: string
  addressMatchesWill: boolean
  addressMismatchNotes: string
  phoneNumber: string
  email: string
  relationshipToDeceased: string
  dateOfBirth: string
  /**
   * @TODO The split date of birth fields will be dropped once any reporting
   * has been updated. Any changes to the old values will be copied
   * over to the new date_of_birth column via a PostgresQL trigger.
   */
  birthYear: number
  birthMonth: number
  birthDay: number
  personalRepresentativeRole: string
  isInsolvent: boolean
  authorizedToDealWithCase: boolean
  authorizedToBeIncludedInComms: boolean
  isDeleted: boolean
  addressId: number
  text: string
  notes: string
  customRelationshipToDeceased: string
  personalRepresentativeType: string
  isBeneficiary: boolean
  isDependent: boolean
  canBeContacted: boolean | null
  canBeContactedReason: string | null
  isActingOnBehalfOfSomeoneElse: boolean
  powerOfAttorneyOrOrderType: string
  powerOfAttorneyRegistered: boolean
  applyingOnBehalfOf: string
  isActingOnBehalfOfSomeoneWithCapacityToAct: boolean
  probateEstate: ProbateEstate
  address: Address
  reference: string
  kycChecksSentAt: Date | null
  kycChecksPassedAt: Date | null
  kycChecksPassedAtBy: number
  originalContactId: number
  signatureRequests?: (SignatureRequest & {
    documentName: ProbateSignatureRequestDocument
  })[]
}

export type ProbateEstatePerson = {
  id: number
  type: 'probate_estate_people'
  attributes: Partial<ProbateEstatePeopleAttributes>
}

export type ProbateEstatePropertiesAttributes = {
  itemType: string
  isMainResidence: StringBoolean | boolean
  deceasedShareOfJointOwnership: string
  registryCheckStatus: string
  value: string
  ownership: string
  jointOwnershipType: string
  hasCharges: StringBoolean
  deceasedShareOfChargesValue: '' | number
  addressId: number
  address: Address
  tenure: string | null
  isEstimatedValue: boolean | null
  propertyType: string | null
  coOwnerIds: number[]
  isCurrentlyOccupied: boolean | null
  isInsured: string | null
  insuranceProviderName: string | null
  insurancePolicyNumber: string | null
  notes: string | null
}

export type ProbateEstateProperty = {
  id: number
  type: 'probate_estate_properties'
  attributes: Partial<ProbateEstatePropertiesAttributes>
}

// mirrors server/src/probate/api/probate-estates/advisors/schemata.ts
export type ProbateEstateAdvisorAttributes = {
  probateEstateId: number
  fullName: string | null
  accountReference: string | null
  email: string | null
  phoneNumber: string | null
  notes: string | null
}

export type ProbateEstateAdvisor = {
  id: number
  type: 'probate_estate_advisors'
  attributes: Partial<ProbateEstateAdvisorAttributes>
}
